import React, { useState } from 'react'
import { isEqual, uniqBy, flatten } from 'lodash'
import pluralize from 'pluralize'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'
import { ModalActions } from '@/components/modals/ModalActions'
import { DeleteCampaignModal } from '@/views/PlaybookMapping/components/DeleteCampaignModal'
import { createDeleteConfirmationContent } from '@/views/PlaybookMapping/helpers'
import { Button } from '@/components/buttons/Button'
import Select from '@/components/forms/Select'
import { FormField } from '@/components/forms/FormField'

import { TABLE_COLUMNS } from '../constants'
import { EditCampaignModal } from './EditCampaignModal'

export const CampaignTable = ({
  rows,
  loading,
  onEditSubmit,
  onDelete,
  onBulkUpdate,
  onBulkDelete,
  dropdownOptions,
  modalLoading,
}) => {
  const [rowIdsRendered, setRowIdsRendered] = useState([])
  const [bulkEditCampaign, setBulkEditCampaign] = useState(false)
  const [bulkDeleteCampaign, setBulkDeleteCampaign] = useState(false)
  const [selectedPlaybook, setSelectedPlaybook] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])
  const selectedCampaigns = rows
    .filter((row) => selectedRows.includes(row.voipCampaignName))
    .map((row) => row.originalCampaign)

  const updateRowIdsRendered = (rowIds) => {
    if (!isEqual(rowIds, rowIdsRendered)) {
      setRowIdsRendered(rowIds)
    }
  }

  const handleBulkUpdatePlaybook = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault()
    }

    if (!selectedPlaybook) {
      return
    }

    const mappedCampaigns = selectedCampaigns.filter((campaign) => campaign.uuid)
    const unmappedCampaigns = selectedCampaigns.filter((campaign) => campaign.isUnmatched)
    const campaignUuids = mappedCampaigns.map((campaign) => campaign.uuid)
    const campaignNames = unmappedCampaigns.map((campaign) => campaign.name)

    await onBulkUpdate({ campaignUuids, campaignNames, cid: selectedPlaybook })

    setBulkEditCampaign(false)
    setSelectedPlaybook(null)
    setSelectedRows([])
  }

  const actions = [
    {
      label: 'Edit',
      trigger: (row) => (
        <EditCampaignModal
          onSubmit={onEditSubmit}
          editingCampaign={row.originalCampaign}
          dropdownOptions={dropdownOptions}
          loading={modalLoading}
        />
      ),
    },
    {
      label: 'Delete',
      trigger: (row) => (
        <DeleteCampaignModal
          onDelete={onDelete}
          campaigns={[row.originalCampaign]}
          useIcon
          disabled={row.originalCampaign?.isUnmatched}
        />
      ),
    },
  ]

  const handleBulkSelectSingle = (row) => {
    if (!selectedRows.includes(row)) {
      setSelectedRows([...selectedRows, row])
    } else {
      setSelectedRows(selectedRows.filter((selectedRow) => selectedRow !== row))
    }
  }

  const handleSelectAll = (rows) => {
    setSelectedRows(rows.map((row) => row.voipCampaignName))
  }

  const handleDeselectAll = () => {
    setSelectedRows([])
  }

  const deletableCampaigns = selectedCampaigns.filter((campaign) => campaign.uuid)
  const hasUnmappedSelected = selectedCampaigns.some((campaign) => campaign.isUnmatched)

  const bulkUpdateDisabledReason = !selectedPlaybook
    ? 'Please select a playbook to update'
    : selectedCampaigns.length === 0
      ? 'No campaigns selected'
      : null

  const bulkDeleteDisabledReason =
    deletableCampaigns.length === 0
      ? hasUnmappedSelected
        ? 'Cannot delete unmapped campaigns'
        : 'No campaigns selected for deletion'
      : null

  const handleBulkDelete = async () => {
    if (deletableCampaigns.length > 0) {
      const uuids = deletableCampaigns.map((campaign) => campaign.uuid)
      await onBulkDelete({ campaignUuids: uuids })
      setSelectedRows([])
      setBulkDeleteCampaign(false)
    }
  }

  const isSingle = deletableCampaigns.length === 1
  const affectedScorecards = uniqBy(
    flatten(deletableCampaigns.map((c) => c.scorecard_configs || [])),
    'id'
  )

  const bulkDeleteFormContent = createDeleteConfirmationContent(
    deletableCampaigns,
    affectedScorecards
  )

  return (
    <Card className="no-padding mb">
      <AdvancedTable
        rows={rows}
        columns={TABLE_COLUMNS}
        actions={actions}
        pagination
        loading={loading}
        loadingText="Loading campaigns..."
        stickyAction
        bulkSelectable
        updateRowIdsRendered={updateRowIdsRendered}
        selectedRows={selectedRows}
        disabledSelection={false}
        handleBulkSelectSingle={handleBulkSelectSingle}
        handleDeselectAll={handleDeselectAll}
        bulkSelectAction={
          <div className="flex-align-center medium-gap">
            <Button
              onClick={(event) => {
                event.preventDefault()
                setBulkEditCampaign(true)
              }}
              small
              disabled={selectedCampaigns.length === 0}
            >
              Update Playbook
            </Button>
            <Button
              critical
              small
              onClick={() => {
                setBulkDeleteCampaign(true)
              }}
              disabled={!!bulkDeleteDisabledReason}
              disabledTooltip={bulkDeleteDisabledReason}
            >
              Delete Selected
            </Button>
          </div>
        }
        handleSelectAll={handleSelectAll}
        itemName="VoIP Campaign mapping"
        index="voipCampaignName"
      />

      {bulkEditCampaign && (
        <BasicModal
          title="Update Playbook"
          onClose={() => {
            setBulkEditCampaign(false)
            setSelectedPlaybook(null)
          }}
          show={bulkEditCampaign}
          size="tiny"
        >
          <form className="field-stack" onSubmit={handleBulkUpdatePlaybook}>
            <FormField>
              <label>
                {`Update Playbook for ${selectedCampaigns.length} Campaign${selectedCampaigns.length > 1 ? 's' : ''}`}
              </label>
              <Select
                placeholder="Select Playbook"
                options={dropdownOptions}
                name="config_cid"
                onChange={(option) => {
                  setSelectedPlaybook(option?.value)
                }}
                value={selectedPlaybook}
                required
              />
            </FormField>
            <div className="modal-footer">
              <Button
                data-testid="dismiss-button"
                content="Dismiss"
                onClick={() => {
                  setBulkEditCampaign(false)
                  setSelectedPlaybook(null)
                }}
              />
              <Button
                type="submit"
                brand
                disabled={!!bulkUpdateDisabledReason || modalLoading}
                disabledTooltip={bulkUpdateDisabledReason}
                content="Apply"
                loading={modalLoading}
                data-testid="submit-update-playbooks-button"
              />
            </div>
          </form>
        </BasicModal>
      )}

      {bulkDeleteCampaign && (
        <BasicModal
          title={`Delete ${pluralize('Campaign', deletableCampaigns.length, true)}${isSingle ? ` "${deletableCampaigns[0].name}"` : ''}`}
          onClose={() => setBulkDeleteCampaign(false)}
          show={bulkDeleteCampaign}
          size="tiny"
        >
          {bulkDeleteFormContent}
          <ModalActions
            onClose={() => setBulkDeleteCampaign(false)}
            onSubmit={handleBulkDelete}
            isDelete
          />
        </BasicModal>
      )}
    </Card>
  )
}
